import React from 'react';
import { filesize } from 'filesize';

import { useConvertFileType } from '@lyearn/academy-app/src/@/hooks/useConvertFile/types';
import isPdf from '@lyearn/core/utils/isPdf';
import { getSanitisedUrl } from '@lyearn/core/utils/url';
import { useTranslation } from '@lyearn/i18n';
import { EmbedPlaceholder, Link2Outline } from '@lyearn/icons';
import { useReactNodeView, useRequestContext } from '@lyearn/lygo-core';
import { Stack, Typography, useSnackbar } from '@lyearn/ui';

import { EmbedPopover } from '../../../components/EmbedPopover';
import ReactPdf from '../../../components/ReactPdf';
import RedirectLinkEditor from '../../../components/RedirectLinkEditor';
import { UploadCard } from '../../../components/UploadCard';
import useDrop from '../../../hooks/useDrop';
import { usePortalForForms } from '../../../hooks/usePortalForForms';
import useUpload, { FileType } from '../../../hooks/useUpload';
import BlockTitleAndDescription from '../../Image/plugins/blockTitleAndDescription';
import { EmbedComponentProps } from '../types';
import Iframe from './components/Iframe';
import { PDF_PER_PAGE_DURATION } from './consts';
import { getSanitisedDocIfContainsScript } from './utils';

const CONVERTIBLE_FILES = ['ppt', 'pptx', 'doc', 'docx'];

const validExtensionTypes = ['pdf', ...CONVERTIBLE_FILES].map((ext) => `.${ext}`).join(',');

const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation();

const EmbedComponentEditable: React.FC<EmbedComponentProps> = (props) => {
  const { href, id, name, embedUrl, aspectRatio, jobToken, srcDoc, type } = props;
  const { useConvertFile, enabledConvertibleImport } = useRequestContext();
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();

  const { node, view, updateAttrs, isMicroLearning = false } = useReactNodeView();
  const formRef = React.useRef<HTMLDivElement>(null);
  const updateEditorState = React.useCallback(
    (file: FileType) => {
      updateAttrs({
        href: file.url,
        name: file.name,
        type: file.extension,
        size: filesize(file.size),
      });
    },
    [updateAttrs],
  );

  const updateDuration = React.useCallback(
    (duration: number) => {
      if (view?.editable && node?.attrs.duration !== duration) {
        updateAttrs({ duration: duration });
      }
    },
    [node?.attrs.duration, updateAttrs, view?.editable],
  );

  const { uppy, currentFile } = useUpload(updateEditorState, node?.attrs.id);
  useDrop(node, updateAttrs, uppy);

  const {
    isProcessing = false,
    data: processingData,
    error,
  } = useConvertFile?.({
    src: href,
    type,
    jobToken,
    targetType: 'pdf',
  }) ?? ({} as ReturnType<useConvertFileType>);

  React.useEffect(() => {
    if (!processingData) {
      return;
    }

    const { outputSrc, outputType, jobToken } = processingData;

    if (jobToken) {
      return updateAttrs({ jobToken });
    }

    if (outputSrc && outputType) {
      return updateAttrs({ href: outputSrc, type: outputType, jobToken: jobToken });
    }
  }, [processingData, updateAttrs]);

  React.useEffect(() => {
    if (error) {
      showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
    }
  }, [error, showErrorSnackbar, t]);

  const setEmbedUrl = React.useCallback(
    (url: string) => {
      const sanitizedSrcDocs = getSanitisedDocIfContainsScript(url);
      if (sanitizedSrcDocs) {
        updateAttrs({ srcDoc: sanitizedSrcDocs });
      } else {
        updateAttrs({ embedUrl: getSanitisedUrl(url) });
      }
    },
    [updateAttrs],
  );

  const setAspectRatio = React.useCallback(
    (aspectRatio: number) => {
      updateAttrs({ aspectRatio });
    },
    [updateAttrs],
  );

  const onDocumentLoadSuccess = React.useCallback(
    (numPages: number) => {
      updateDuration?.(numPages * PDF_PER_PAGE_DURATION);
    },
    [updateDuration],
  );

  const onPageLoadSuccess = React.useCallback(
    (width: number, height: number) => {
      if (!aspectRatio) {
        setAspectRatio?.(width / height);
      }
    },
    [aspectRatio, setAspectRatio],
  );

  const isUploading = !!currentFile && !currentFile.url;

  const isPdfContent = type === 'pdf' || (href && isPdf(href)) || (embedUrl && isPdf(embedUrl));
  const isIframeContent = !isPdfContent && Boolean(embedUrl || srcDoc);

  // Title/description authoring for iframe embeds in micro-learning, rendered into a floating
  // portal (same pattern the PDF/Video/Image cards use). Paused unless we're actually showing
  // the iframe micro-learning card, so it doesn't spawn an empty container for other states.
  const embedAttributesFrom = usePortalForForms(
    formRef,
    <Stack className="" direction="col" gapY={10}>
      <BlockTitleAndDescription />
    </Stack>,
    !(isMicroLearning && isIframeContent),
  );

  if (isPdfContent) {
    return (
      <div className="my-48">
        <ReactPdf
          aspectRatio={aspectRatio}
          documentLoadSuccessCallback={onDocumentLoadSuccess}
          name={name?.split('.pdf')[0]}
          pageLoadSuccessCallback={onPageLoadSuccess}
          src={href ? href : embedUrl!}
        />
      </div>
    );
  }

  //TODO: add resizeable wrapper on iFrame

  if (isIframeContent) {
    const iframe = <Iframe src={embedUrl} srcDoc={srcDoc} />;

    if (!isMicroLearning) {
      return iframe;
    }

    return (
      <Stack
        className="bg-bg-hover-light mt-24 w-full overflow-hidden rounded-16 border border-border"
        direction="col"
        onClick={stopPropagation}
        onMouseDown={stopPropagation}>
        <Stack
          alignItems="center"
          className="h-[6.2rem] p-12 border-b border-border"
          justifyContent="between">
          <Stack alignItems="center" gapX={12}>
            <Link2Outline className="h-24 w-24" fillColorToken="icon-secondary" />
            <Stack direction="col">
              <Typography numberOfLines={1} variant="body-short-02">
                {name ?? embedUrl ?? 'Embed'}
              </Typography>
              <Typography className="text-secondary" variant="body-short-03">
                Preview
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack className="w-full p-24" direction="col" gapY={16}>
          {iframe}
          <Stack ref={formRef} className="h-[11rem] w-full" direction="col">
            {embedAttributesFrom}
          </Stack>
          <RedirectLinkEditor />
        </Stack>
      </Stack>
    );
  }

  if (isUploading || isProcessing) {
    return (
      <div className="mt-16">
        <UploadCard
          PlaceholderIcon={EmbedPlaceholder}
          isProcessing={isProcessing}
          isUploading={isUploading}
          name={currentFile?.name ?? name}
          progress={currentFile?.fileProgress as number}
        />
      </div>
    );
  }

  return (
    <EmbedPopover
      PlaceholderIcon={EmbedPlaceholder}
      id={id!}
      inputButtonString={t('Lygo:Embed')}
      inputPlaceholderString={t('Lygo:orPasteLink')}
      placeholderString={
        enabledConvertibleImport ? t('Lygo:EmbedPlaceholder') : t('Lygo:EmbedPdfPlaceholder')
      }
      setEmbedUrl={setEmbedUrl}
      validFileTypes={enabledConvertibleImport ? validExtensionTypes : '.pdf'}
    />
  );
};

export default EmbedComponentEditable;
